// @flow

import moment from 'moment';
import {
  formatDateDB,
  toMoment,
} from '@fortress-technology-solutions/fortress-component-library/utils';
import {
  getHudGrossRentSnapshotFee,
  getHudUaSnapshotFee,
  getLatestActiveScheduleRow,
  processFloorPlanWithPricingResult,
} from '../../ManageFloorPlans/HudLimitsModal/utils';
import { formatBedsBathLabel } from '../GrossRentChangesTab/utils';
import type { GrossRentChangeGroup } from '../GrossRentChangesTab/types';
import type {
  AddGrossRentLimitsFormValues,
  ChangedFloorPlanRow,
  EditDatesPayload,
  EditDatesValues,
  FloorPlanChangeValues,
  HudFloorPlanRow,
} from './types';

const HUD = 'HUD';
const HUD_GROSS_RENT = 'HUD_GROSS_RENT';
const UTILITY_ALLOWANCE = 'UTILITY_ALLOWANCE';

// Parses a date-only DB string (YYYY-MM-DD) as a local-midnight Date.
// `toMoment` parses ISO date-only strings as UTC, which shifts the calendar
// day backwards in negative-offset timezones (e.g. 2026-06-01 -> May 31 in
// GMT-6). The DatePicker renders in local time, so we anchor to local midnight.
export const parseDbDateLocal = (value: ?string): ?Date => {
  if (!value) {
    return null;
  }

  const parsed = moment(value, 'YYYY-MM-DD', true);

  return parsed.isValid() ? parsed.toDate() : null;
};

export const computeAnticipatedVoucherMonthForGrossRent = (
  effectiveDate: ?Date,
): ?Date => {
  if (!effectiveDate) {
    return null;
  }

  const todayDate = moment();
  const effectiveMoment = moment(effectiveDate);

  if (effectiveMoment.isBefore(todayDate, 'day')) {
    return todayDate.clone().add(1, 'months').startOf('month').toDate();
  }

  const dayOfMonth = effectiveMoment.date();
  const firstOfMonth = effectiveMoment.clone().startOf('month');

  if (dayOfMonth === 1) {
    return firstOfMonth.toDate();
  }

  return firstOfMonth.clone().add(1, 'months').toDate();
};

export const mapHudFloorPlanRows = (
  floorPlans: Array<Object>,
  effectiveDate: ?Date,
): Array<HudFloorPlanRow> => {
  const effectiveDateParam = effectiveDate ? formatDateDB(effectiveDate) : null;
  return processFloorPlanWithPricingResult(floorPlans).map((floorPlan) => ({
    floorPlanId: floorPlan.id,
    floorPlanName: floorPlan.internalName ?? floorPlan.marketingName ?? '',
    bedsBath: formatBedsBathLabel(
      floorPlan.nBeds ?? floorPlan.numberOfBedrooms ?? 0,
      floorPlan.nBaths ?? floorPlan.numberOfBathrooms ?? 0,
    ),
    currentGrossRent: getHudGrossRentSnapshotFee(floorPlan, effectiveDateParam),
    currentUtilityAllowance: getHudUaSnapshotFee(floorPlan, effectiveDateParam),
  }));
};

export const validatePositiveAmount = (
  value: ?number | string,
  errorMessage: string,
) => {
  if (value === null || value === undefined || value === '') {
    return true;
  }

  const numericValue = Number(value);
  if (Number.isNaN(numericValue) || numericValue <= 0) {
    return errorMessage;
  }

  return true;
};

const hasPositiveChange = (change: ?FloorPlanChangeValues) => {
  if (!change) {
    return false;
  }

  const grossRent = Number(change.newGrossRent);
  const utilityAllowance = Number(change.newUtilityAllowance);

  const hasGrossRent = !Number.isNaN(grossRent) && grossRent > 0;
  const hasUtilityAllowance =
    !Number.isNaN(utilityAllowance) && utilityAllowance > 0;

  return hasGrossRent || hasUtilityAllowance;
};

const toPositiveAmount = (value: ?number | string): ?number => {
  const numericValue = Number(value);
  if (Number.isNaN(numericValue) || numericValue <= 0) {
    return null;
  }

  return numericValue;
};

// Resolves the active HUD utility-allowance schedule row for a raw floor plan,
// exposing both the `utilityAlowancesId` (column typo is canonical) and the PHA
// name. Shared by the batch-create and edit payload builders.
const resolveFloorPlanUtilityAllowance = (
  floorPlan: ?Object,
): {| utilityAlowancesId: string, name: string |} => {
  const fpua = floorPlan?.fpua ?? [];
  const isFpUa = fpua.length > 0;
  const allowances = isFpUa ? fpua : floorPlan?.allowances ?? [];
  const latestUa = getLatestActiveScheduleRow(
    allowances,
    getHudUaSnapshotFee(floorPlan ?? {}),
  );

  return {
    utilityAlowancesId: isFpUa
      ? latestUa?.utilityAlowancesId ?? ''
      : latestUa?.id ?? '',
    name: isFpUa ? latestUa?.ua?.pha?.name ?? '' : latestUa?.pha?.name ?? '',
  };
};

export const getChangedFloorPlanRows = (
  floorPlans: Array<HudFloorPlanRow>,
  floorPlanChanges: {
    [floorPlanId: string]: FloorPlanChangeValues,
  },
): Array<ChangedFloorPlanRow> =>
  floorPlans
    .filter((floorPlan) =>
      hasPositiveChange(floorPlanChanges[floorPlan.floorPlanId]),
    )
    .map((floorPlan) => {
      const change = floorPlanChanges[floorPlan.floorPlanId] ?? {};

      return {
        ...floorPlan,
        newGrossRent: toPositiveAmount(change.newGrossRent),
        newUtilityAllowance: toPositiveAmount(change.newUtilityAllowance),
      };
    });

export const deriveGrcExistsForYearFromCertifications = (
  certifications: Array<Object>,
  year: number,
): boolean =>
  certifications.some(({ grEffectiveDate, anticipatedVoucherDate }) => {
    const date = grEffectiveDate ?? anticipatedVoucherDate;
    if (typeof date !== 'string' || date.length === 0) {
      return false;
    }

    return toMoment(date).year() === year;
  });

export const buildBatchPayload = (
  values: AddGrossRentLimitsFormValues,
  floorPlans: Array<Object>,
): Array<Object> => {
  if (!values.effectiveDate) {
    return [];
  }

  const startDate = formatDateDB(values.effectiveDate);
  const floorPlanChanges = values.floorPlanChanges ?? {};
  const result = [];

  const normalizedSelectedAvd = values.anticipatedVoucherMonth
    ? formatDateDB(
        moment(values.anticipatedVoucherMonth).startOf('month').toDate(),
      )
    : null;
  const { autoCalculatedAnticipatedVoucherMonth } = values;
  const normalizedAutoCalculatedAvd = autoCalculatedAnticipatedVoucherMonth
    ? formatDateDB(
        moment(autoCalculatedAnticipatedVoucherMonth).startOf('month').toDate(),
      )
    : null;
  const anticipatedVoucherDateOverride =
    normalizedSelectedAvd &&
    normalizedSelectedAvd !== normalizedAutoCalculatedAvd
      ? normalizedSelectedAvd
      : null;

  Object.keys(floorPlanChanges).forEach((floorPlanId) => {
    const change = floorPlanChanges[floorPlanId];
    const floorPlan = floorPlans.find((fp) => fp.id === floorPlanId) ?? {};
    const newGrossRent = toPositiveAmount(change?.newGrossRent);

    if (newGrossRent != null) {
      const entry: Object = {
        floorPlanId,
        pricingType: HUD_GROSS_RENT,
        feeAmount: newGrossRent,
        startDate,
      };
      if (anticipatedVoucherDateOverride) {
        entry.anticipatedVoucherDateOverride = anticipatedVoucherDateOverride;
      }

      result.push(entry);
    }

    const utilityAllowanceAmount = toPositiveAmount(
      change?.newUtilityAllowance,
    );

    if (utilityAllowanceAmount != null) {
      const { utilityAlowancesId, name } =
        resolveFloorPlanUtilityAllowance(floorPlan);

      const entry: Object = {
        floorPlanId,
        pricingType: `${name || HUD}_${UTILITY_ALLOWANCE}`,
        feeAmount: utilityAllowanceAmount,
        startDate,
      };
      // A never-configured floor plan has no UA source to reference; omit the
      // id so the backend resolves or creates one.
      if (utilityAlowancesId) {
        entry.utilityAlowancesId = utilityAlowancesId;
      }
      if (anticipatedVoucherDateOverride) {
        entry.anticipatedVoucherDateOverride = anticipatedVoucherDateOverride;
      }

      result.push(entry);
    }
  });

  return result;
};

export type StepOneValidationValues = {|
  effectiveDate: ?Date,
  anticipatedVoucherMonth: ?Date,
  floorPlanChanges: {
    [floorPlanId: string]: FloorPlanChangeValues,
  },
|};

export const isStepOneValid = (values: StepOneValidationValues): boolean => {
  if (!values.effectiveDate || !values.anticipatedVoucherMonth) {
    return false;
  }

  const floorPlanChanges = values.floorPlanChanges ?? {};
  return Object.keys(floorPlanChanges).some((floorPlanId) =>
    hasPositiveChange(floorPlanChanges[floorPlanId]),
  );
};

const indexSavedFloorPlansById = (
  editingChange: ?GrossRentChangeGroup,
): { [floorPlanId: string]: Object } => {
  const savedByFloorPlanId = {};
  (editingChange?.floorPlans ?? []).forEach((floorPlan) => {
    savedByFloorPlanId[floorPlan.floorPlanId] = floorPlan;
  });
  return savedByFloorPlanId;
};

export const resolveSavedAnticipatedVoucherMonth = (
  editingChange: ?GrossRentChangeGroup,
): ?Date => {
  const savedDate =
    parseDbDateLocal(editingChange?.anticipatedVoucherDate) ??
    computeAnticipatedVoucherMonthForGrossRent(
      parseDbDateLocal(editingChange?.effectiveDate),
    );

  return savedDate ? moment(savedDate).startOf('month').toDate() : null;
};

// Prefills the wizard form from a saved gross rent change so edit mode opens
// with the existing amounts, effective date, and anticipated voucher month.
export const buildEditDefaultFormValues = (
  editingChange: ?GrossRentChangeGroup,
): AddGrossRentLimitsFormValues => {
  const floorPlanChanges = {};
  (editingChange?.floorPlans ?? []).forEach((floorPlan) => {
    floorPlanChanges[floorPlan.floorPlanId] = {
      newGrossRent: floorPlan.grossRent ?? null,
      newUtilityAllowance: floorPlan.utilityAllowance ?? null,
    };
  });

  return {
    effectiveDate: parseDbDateLocal(editingChange?.effectiveDate),
    anticipatedVoucherMonth: resolveSavedAnticipatedVoucherMonth(editingChange),
    floorPlanChanges,
  };
};

export const buildEditDatesPayload = (
  values: EditDatesValues,
  editingChange: ?GrossRentChangeGroup,
): EditDatesPayload => {
  const payload: EditDatesPayload = {};

  if (values.effectiveDate) {
    const newEffectiveDate = formatDateDB(values.effectiveDate);
    if (newEffectiveDate !== editingChange?.effectiveDate) {
      payload.newEffectiveDate = newEffectiveDate;
    }
  }

  if (values.anticipatedVoucherMonth) {
    const newAnticipatedVoucherDate = formatDateDB(
      moment(values.anticipatedVoucherMonth).startOf('month').toDate(),
    );
    const savedAnticipatedVoucherMonth =
      resolveSavedAnticipatedVoucherMonth(editingChange);
    const savedAnticipatedVoucherDate = savedAnticipatedVoucherMonth
      ? formatDateDB(savedAnticipatedVoucherMonth)
      : null;

    if (newAnticipatedVoucherDate !== savedAnticipatedVoucherDate) {
      payload.newAnticipatedVoucherDate = newAnticipatedVoucherDate;
    }
  }

  return payload;
};

// Diff-only edit payload: include a gross rent / utility allowance for a floor
// plan when the form value is a positive number AND differs from the saved
// value (additions included — saved was null or the floor plan wasn't in the
// change). UA additions carry `utilityAlowancesId` so the backend can create
// the missing pricing row. Returns [] when nothing changed.
export const buildEditChangesPayload = (
  values: AddGrossRentLimitsFormValues,
  editingChange: ?GrossRentChangeGroup,
  rawFloorPlans: Array<Object>,
): Array<Object> => {
  const floorPlanChanges = values.floorPlanChanges ?? {};
  const savedByFloorPlanId = indexSavedFloorPlansById(editingChange);
  const changes = [];

  Object.keys(floorPlanChanges).forEach((floorPlanId) => {
    const change = floorPlanChanges[floorPlanId];
    const saved = savedByFloorPlanId[floorPlanId];
    const savedGrossRent = toPositiveAmount(saved?.grossRent);
    const savedUtilityAllowance = toPositiveAmount(saved?.utilityAllowance);
    const newGrossRent = toPositiveAmount(change?.newGrossRent);
    const newUtilityAllowance = toPositiveAmount(change?.newUtilityAllowance);

    const includeGrossRent =
      newGrossRent != null && newGrossRent !== savedGrossRent;

    const utilityAllowanceToSave =
      newUtilityAllowance != null &&
      newUtilityAllowance !== savedUtilityAllowance
        ? newUtilityAllowance
        : null;

    if (!includeGrossRent && utilityAllowanceToSave == null) {
      return;
    }

    const entry: Object = { floorPlanId };

    if (includeGrossRent) {
      entry.grossRent = newGrossRent;
    }

    if (utilityAllowanceToSave != null) {
      entry.utilityAllowance = utilityAllowanceToSave;

      // Only a UA addition (no existing saved value) needs the id — updates are
      // resolved server-side by the existing pricing row.
      if (savedUtilityAllowance == null) {
        const floorPlan = (rawFloorPlans ?? []).find(
          (fp) => fp.id === floorPlanId,
        );
        const { utilityAlowancesId } =
          resolveFloorPlanUtilityAllowance(floorPlan);
        if (utilityAlowancesId) {
          entry.utilityAlowancesId = utilityAlowancesId;
        }
      }
    }

    changes.push(entry);
  });

  return changes;
};
